/**
 * Typed models for the legacy heavy `POLYLINE`, `ACAD_TABLE` and `OLE2FRAME`
 * entities.
 *
 * Extracted as a leaf module (the `A08B`…`A08L` per-entity-family convention
 * documented in `./entityBase`): it imports the shared primitives from
 * `./entityBase` and nothing flows back from `model.ts`, so no circular
 * import. `model.ts` adds these schemas to `drawingEntitySchema` /
 * `blockChildEntitySchema` and re-exports the inferred types.
 *
 * Source of truth: Autodesk DXF reference + ezdxf 1.4.4
 * (`entities/polyline.py`, `entities/acad_table.py`, `entities/oleframe.py`).
 * AutoCAD MCP is BANNED.
 */
import { z } from "zod";

import {
  DXF_POLYLINE_FLAG,
  dxfPolylineSubtype,
  type DxfCommonEntityProps,
  type DxfSeqendEntity,
} from "@dxf-ai-editor/dxf-io";

/**
 * Re-exported so consumers that only depend on `drawing-model` (command-engine
 * and, through it, the property descriptors) read the group-70 bit values from
 * the SAME dxf-io definition the parser and writer use — rather than
 * re-declaring a second copy that could drift from the file format.
 */
export {
  DXF_POLYLINE_FLAG,
  DXF_VERTEX_FLAG,
  dxfVertexSubtype,
} from "@dxf-ai-editor/dxf-io";

import { drawingPoint3DSchema, drawingEntityBaseSchema } from "./entityBase";

/**
 * One raw DXF group, used by the ACAD_TABLE content model. Structurally the
 * same `{code, value}` pair `rawEntityGroupSchema` (model.ts) stores, but
 * declared here so this leaf does not import back from `model.ts`.
 */
export const dxfGroupRecordSchema = z.object({
  code: z.number().int(),
  value: z.string(),
});
export type DxfGroupRecord = z.infer<typeof dxfGroupRecordSchema>;

/* ── POLYLINE (legacy "heavy" polyline) ───────────────────────────────── */

/**
 * One `1001`-rooted XDATA block of a `VERTEX` record: the application id
 * (group 1001) plus its trailing `1000`…`1071` groups, in file order.
 * Structurally identical to dxf-io's `DxfXDataEntry`, declared here rather
 * than imported: a model schema the editor READS AND EDITS states its own
 * shape, so a change in the parser's types cannot silently redefine what a
 * document is (and this leaf must not import from `model.ts` — see the module
 * header).
 *
 * {@link PolylineSeqendMetadata} below is the deliberate exception, and for
 * the opposite reason: it is VERBATIM PASSTHROUGH the editor never reads, so
 * it must track the parsed record EXACTLY. Typing it as `Omit<DxfSeqendEntity,
 * …>` makes the coupling the guarantee — a channel dxf-io learns to parse is
 * carried automatically instead of being dropped on the next save.
 */
export const dxfXDataEntryRecordSchema = z.object({
  appName: z.string(),
  groups: z.array(dxfGroupRecordSchema),
});
export type DxfXDataEntryRecord = z.infer<typeof dxfXDataEntryRecordSchema>;

/**
 * One `VERTEX` record of a heavy POLYLINE.
 *
 * ## Why the vertex carries a handle but no layer / colour
 *
 * A VERTEX is a full DXF entity in the file, but its layer, linetype,
 * lineweight and colour are NOT independently editable in AutoCAD: they
 * always mirror the owning POLYLINE. ezdxf encodes exactly that with
 * `Polyline.on_layer_change` / `on_linetype_change`, which rewrite every
 * child vertex whenever the polyline's own value changes. Re-deriving those
 * fields from the parent at export time is therefore the FAITHFUL model, not
 * a simplification — storing a second, independently-mutable copy per vertex
 * would invent state AutoCAD does not have.
 *
 * The `handle` is the one genuinely per-vertex datum (it is what
 * `ACDBASSOCGEOMDEPENDENCY` and reactor records point at), so it is kept.
 * This mirrors `blockInsertAttributeSchema`, which likewise keeps the child
 * ATTRIB's handle and derives the rest.
 *
 * ## Why the record ALSO carries the four passthrough channels
 *
 * Derived-from-the-parent applies to the AcDbEntity fields AutoCAD itself
 * rewrites (layer / linetype / colour). It does NOT apply to the records
 * that belong to this VERTEX alone and that no other field can reconstruct:
 *
 *   - `xdata`                     — `1001 <APPID>` blocks a third-party
 *                                   application attached to THIS vertex;
 *   - `extensionDictionaryHandle` — the 360 inside `102 {ACAD_XDICTIONARY`;
 *   - `extraGroups`               — the verbatim `102 {ACAD_REACTORS … 102 }`
 *                                   block plus any group the VERTEX reader
 *                                   does not map to a typed field;
 *   - `subclassMarkers`           — a non-canonical `100 …` subclass
 *                                   extension the file carried.
 *
 * Before the heavy polyline became a typed primitive, `POLYLINE`/`VERTEX`
 * were `unsupported-entity` records and every one of those groups survived
 * as verbatim byte passthrough. A typed record that kept only the handle and
 * the geometry therefore DELETED user content on the first open-and-save —
 * so the four channels are stored here and re-emitted by the export bridge.
 * They are the SAME four channels `drawingEntityBaseSchema` /
 * `DxfSupportedEntityBase` give a top-level entity, not a new mechanism.
 */
export const polylineVertexRecordSchema = z.object({
  /** DXF group 5 — the VERTEX record's own handle. */
  handle: z.string().default(""),
  /** 10/20/30 — vertex location (OCS on a 2D polyline, WCS on a 3D one). */
  location: drawingPoint3DSchema,
  /** 40 — starting width; absent ⇒ inherit the polyline's default. */
  startWidth: z.number().optional(),
  /** 41 — ending width; absent ⇒ inherit the polyline's default. */
  endWidth: z.number().optional(),
  /** 42 — bulge, tan(θ/4) of the arc through this segment; absent ⇒ 0. */
  bulge: z.number().optional(),
  /** 70 — vertex flags (curve-fit / spline / 3D / mesh bits). */
  flags: z.number().int().optional(),
  /** 50 — curve-fit tangent direction in DEGREES; used when flag bit 2 set. */
  curveFitTangent: z.number().optional(),
  /**
   * 71/72/73/74 — poly-face FACE record: the 1-based vertex indices forming
   * this face, in authored order. A negative index marks the edge starting at
   * that vertex invisible. An ordered array (never four scalars) so both a
   * triangular and a quad face are expressible and the winding survives.
   */
  faceVertexIndices: z.array(z.number().int()).optional(),
  /** 91 — persistent vertex identifier. */
  vertexId: z.number().int().optional(),
  /**
   * Ordered `100` subclass markers this VERTEX carried. The canonical
   * `AcDbEntity` / `AcDbVertex` / `AcDb<subtype>Vertex` / `AcDbFaceRecord`
   * markers are a FUNCTION OF GROUP 70 and are re-emitted by the writer, so
   * only a NON-canonical extension marker needs this channel — it is stored
   * whole (never filtered) because `mergeSubclassMarkers` already drops the
   * family markers on the way out.
   */
  subclassMarkers: z.array(z.string()).optional(),
  /**
   * Verbatim groups the VERTEX reader did not map to a typed field: the
   * `102 {ACAD_REACTORS … 102 }` control block and any vendor / future code.
   * Same `{code, value}` shape as `drawingEntityBaseSchema.extraGroups`.
   */
  extraGroups: z.array(dxfGroupRecordSchema).optional(),
  /** `1001`-rooted XDATA blocks attached to THIS vertex, in file order. */
  xdata: z.array(dxfXDataEntryRecordSchema).optional(),
  /** The 360 handle inside this VERTEX's `102 {ACAD_XDICTIONARY … 102 }`. */
  extensionDictionaryHandle: z.string().optional(),
});
export type PolylineVertexRecord = z.infer<typeof polylineVertexRecordSchema>;

/* ── the terminating SEQEND ───────────────────────────────────────────── */

/**
 * The record keys the EXPORT bridge re-derives, and that the model therefore
 * must not keep a second copy of. Both halves are structural identity, not
 * record data:
 *
 *   - `kind` / `type` / `geometry` — constants of a SEQEND record
 *     (`"supported-entity"` / `"SEQEND"` / `{}`); the terminator has no body.
 *   - `common.handle` — the SEQEND's own group 5. It already lives in
 *     `PolylineEntity.seqendHandle`, which is the key the conformant
 *     handle machinery mints, claims and censuses (`OWN_HANDLE_KEYS`,
 *     `dxfBridge.conformantSkeleton.handles.ts`). A second copy here would be
 *     a second truth that remint silently leaves stale.
 *   - `common.ownerHandle` — group 330. A POLYLINE OWNS its terminator, so
 *     the owner IS the parent's handle (ezdxf 1.4.4 `Polyline.new_seqend`:
 *     `seqend.dxf.owner = polyline.dxf.handle`; the dxf-io writer applies the
 *     same rule to a bridge-built polyline). Carrying the source value would
 *     dangle the moment the POLYLINE is reminted on a conformant save.
 *   - `common.space` / `common.layoutName` — CONTAINER facts, not groups of
 *     this record: the SEQEND is serialized inline, inside its POLYLINE, in
 *     whatever space/layout the POLYLINE is in, so a carried copy can only go
 *     stale when a command moves the polyline to another layout.
 *   - `common.forceLayerEmit` — a transient writer transport flag that is
 *     never persisted (see its declaration in dxf-io `DxfCommonEntityProps`).
 */
type PolylineSeqendDerivedKey = "kind" | "type" | "geometry";
type PolylineSeqendDerivedCommonKey =
  | "handle"
  | "ownerHandle"
  | "space"
  | "layoutName"
  | "forceLayerEmit";

/**
 * Everything ELSE the terminating `SEQEND` record carried: its own layer,
 * linetype, colour, lineweight, thickness and extrusion, its
 * `102 {ACAD_XDICTIONARY`, its `102 {ACAD_REACTORS` block and any group the
 * SEQEND reader does not map (both in `extraGroups`), a non-canonical `100`
 * subclass extension, and its `1001` XDATA blocks.
 *
 * ## Why this is the REST of the record and not a list of four names
 *
 * The type is the parsed record MINUS the keys above (`Omit` over the very
 * key set the bridge re-derives), so a channel added to `DxfSeqendEntity` /
 * `DxfCommonEntityProps` later is carried AUTOMATICALLY — the import leg
 * rest-destructures the same key set and the export leg spreads the result
 * back. An allow-list of field names is what produced the per-VERTEX loss
 * this repairs one level up (PR #785 round 1) and the `appendEntity` defect
 * before it: the day someone adds a fifth channel, an allow-list silently
 * drops it and nobody notices until a user's file comes back thinner.
 *
 * Before the heavy polyline family became a typed primitive, `POLYLINE`
 * /`VERTEX`/`SEQEND` were `unsupported-entity` records and every one of these
 * groups survived as verbatim byte passthrough. A typed model that kept only
 * `seqendHandle` therefore DELETED them on the first open-and-save — which is
 * why they are stored, even though nothing in the editor reads them.
 */
export type PolylineSeqendMetadata = Omit<
  DxfSeqendEntity,
  PolylineSeqendDerivedKey | "common"
> & { common: Omit<DxfCommonEntityProps, PolylineSeqendDerivedCommonKey> };

/**
 * Structural validation for {@link polylineSeqendMetadataSchema}: the three
 * channels a parsed record always has, plus `xdata`. Every container is LOOSE
 * (`z.looseObject`) — an unknown key is DATA to carry, never a parse error,
 * which is the whole point of a passthrough channel.
 */
const polylineSeqendMetadataShape = z.looseObject({
  common: z.looseObject({}),
  subclassMarkers: z.array(z.string()),
  extraGroups: z.array(dxfGroupRecordSchema),
  xdata: z.array(dxfXDataEntryRecordSchema).optional(),
});

/**
 * The zod face of {@link PolylineSeqendMetadata}. `z.custom` — not a closed
 * `z.object` — because parsing must return the value UNCHANGED: a zod object
 * strips what its shape does not name, which would reintroduce the silent
 * drop this channel exists to prevent. Validation is still real: the shape
 * above is checked, unknown keys are kept.
 */
export const polylineSeqendMetadataSchema = z.custom<PolylineSeqendMetadata>(
  (value) => polylineSeqendMetadataShape.safeParse(value).success,
  { message: "invalid POLYLINE SEQEND metadata record" },
);

/**
 * A heavy `POLYLINE` — AutoCAD's 2D polyline, 3D polyline, polygon mesh and
 * poly-face mesh, all four of which share this one DXF entity and are
 * discriminated by group 70.
 *
 * This is a SEPARATE primitive from `LWPOLYLINE`, not a second truth for it:
 * see `DxfPolylineEntity`'s type doc in `packages/dxf-io/src/
 * types.legacyPolyline.ts` for the enumerated list of things `LWPOLYLINE`
 * provably cannot express (per-vertex Z, M×N meshes, face records,
 * curve/spline-fit vertex bits, per-vertex handles).
 */
export const polylineEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Polyline"),
  /**
   * DXF group 70 — the polyline flags bitfield. THE single source of truth
   * for closed / curve-fit / spline-fit / 3D / mesh / closed-in-N /
   * poly-face / continuous-linetype. Derived reads go through
   * {@link polylineSubtype} and {@link polylineIsClosed} rather than
   * duplicating a decoded copy of the same bits in the model.
   */
  flags: z.number().int().default(0),
  /**
   * 10/20/30 — the "dummy point": X/Y are 0 and Z is the elevation. Kept as
   * the whole point so a file whose X/Y are non-zero round-trips unchanged.
   */
  elevation: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }),
  vertices: z.array(polylineVertexRecordSchema).default([]),
  /** 40 — default starting width for vertices with no own 40. */
  defaultStartWidth: z.number().optional(),
  /** 41 — default ending width for vertices with no own 41. */
  defaultEndWidth: z.number().optional(),
  /** 71 — polygon-mesh M vertex count, or poly-face mesh vertex count. */
  meshMCount: z.number().int().optional(),
  /** 72 — polygon-mesh N vertex count, or poly-face mesh face count. */
  meshNCount: z.number().int().optional(),
  /** 73 — smooth surface M density. */
  smoothSurfaceMDensity: z.number().int().optional(),
  /** 74 — smooth surface N density. */
  smoothSurfaceNDensity: z.number().int().optional(),
  /** 75 — 0 none / 5 quadratic B-spline / 6 cubic B-spline / 8 Bezier. */
  surfaceType: z.number().int().optional(),
  /** 66 — the obsolete "vertices follow" flag, retained if the file had one. */
  verticesFollow: z.number().int().optional(),
  /** Handle of the terminating SEQEND, so the record round-trips with its id. */
  seqendHandle: z.string().optional(),
  /**
   * Everything else the terminating SEQEND record carried — its own layer,
   * XDATA, extension dictionary, reactor block, unmapped groups and subclass
   * extensions. Absent when the polyline was built by a command rather than
   * imported (the writer then synthesises the terminator from this parent).
   * See {@link PolylineSeqendMetadata} for why this is the REST of the record
   * rather than a list of channel names.
   */
  seqendMetadata: polylineSeqendMetadataSchema.optional(),
  /** DXF group 39 — thickness (extrusion depth). */
  thickness: z.number().default(0),
  /** DXF group 210/220/230 — OCS extrusion normal; absent ⇒ (0, 0, 1). */
  extrusion: drawingPoint3DSchema.optional(),
});
export type PolylineEntity = z.infer<typeof polylineEntitySchema>;

/** The four AutoCAD shapes a heavy POLYLINE can take. */
export type PolylineSubtype = "2d" | "3d" | "polygon-mesh" | "polyface-mesh";

/**
 * Classify a heavy polyline from its group-70 flags. Delegates to the ONE
 * implementation in `@dxf-ai-editor/dxf-io` (`dxfPolylineSubtype`, whose bit
 * precedence is copied from ezdxf `Polyline.get_mode`) rather than re-decoding
 * the bits here — one truth, so the model and the DXF writer can never
 * disagree about which subclass a polyline is.
 */
export function polylineSubtype(entity: {
  flags: number;
}): PolylineSubtype {
  return dxfPolylineSubtype(entity.flags);
}

/** Group 70 bit 1 — closed, or (for a mesh) closed in the M direction. */
export function polylineIsClosed(entity: { flags: number }): boolean {
  return (entity.flags & DXF_POLYLINE_FLAG.CLOSED) !== 0;
}

/** Group 70 bit 32 — a polygon mesh closed in the N direction. */
export function polylineIsClosedN(entity: { flags: number }): boolean {
  return (entity.flags & DXF_POLYLINE_FLAG.CLOSED_N) !== 0;
}

/** Group 70 bit 128 — linetype pattern generated continuously (plinegen). */
export function polylineHasContinuousLinetype(entity: {
  flags: number;
}): boolean {
  return (entity.flags & DXF_POLYLINE_FLAG.CONTINUOUS_LINETYPE) !== 0;
}

/* ── ACAD_TABLE ───────────────────────────────────────────────────────── */

/**
 * An AutoCAD table.
 *
 * ## What renders, and why that is faithful rather than a placeholder
 *
 * `ACAD_TABLE` is serialized as a block reference to an ANONYMOUS `*T<N>`
 * block plus the AcDbTable content model. The `*T<N>` block holds the table's
 * fully rendered graphics — the MTEXT of every cell and the LINE grid — the
 * same representation a dynamic block uses with its `*U<N>` block, and it is
 * what AutoCAD itself draws. So the renderer draws that block's content at
 * {@link insertionPoint}, oriented by {@link horizontalDirection}.
 *
 * ## Implemented boundary — read this before extending
 *
 * IMPLEMENTED HERE: parse, typed model, render, bounds, spatial index,
 * picking, selection, layer visibility, properties, and round-trip.
 *
 * NOT IMPLEMENTED: editing cell values, cell formulas, cell styles or merges,
 * TABLESTYLE management, and table breaking. Those are a separate feature.
 * The per-cell records live untouched in {@link contentGroups} so they
 * survive round-trip until that feature lands — their presence is NOT a
 * claim that cell editing works.
 *
 * ALSO NOT IMPLEMENTED: RESIZING a table (GitHub Codex review PR #785 round
 * 2, P2). MOVE / ROTATE / MIRROR are exact — they act on `insertionPoint` and
 * `horizontalDirection`, the only placement state AcDbTable has — but a
 * `geometry/scale-entities` with `|factor| !== 1` has nothing faithful to
 * write: `rowHeights` (141) / `columnWidths` (142) below are READ-ONLY
 * projections of `contentGroups` (the writer emits `contentGroups` verbatim
 * and never re-emits 141/142 from them), and the picture the user sees is the
 * pre-evaluated `*T<N>` graphics block — one MTEXT per cell plus the LINE
 * grid — projected through `tableAsBlockReference`, which hard-codes unit
 * scale because AcDbTable carries no scale factors. Rewriting 141/142 would
 * therefore change the DATA and leave the GRAPHICS identical: a desync worse
 * than not scaling. SCALE consequently DECLINES a table, visibly, with an
 * RU+EN warning-tone audit row — see `command-engine/tableScaleRefusal.ts`
 * for the predicate, the two reducer call sites, and what completing this
 * requires (a `*T<N>` layout engine that rebuilds the block content in the
 * SAME `EntityMutation` as the 141/142 write).
 */
export const tableEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Table"),
  /** DXF group 2 — the anonymous `*T<N>` block carrying the table graphics. */
  blockName: z.string().default(""),
  /** 10/20/30 — insertion point, WCS. */
  insertionPoint: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }),
  /** 11/21/31 — horizontal direction vector; absent ⇒ (1, 0, 0). */
  horizontalDirection: drawingPoint3DSchema.optional(),
  /** 280 — table data version (0 = R2010). */
  version: z.number().int().optional(),
  /** 342 — handle of the TABLESTYLE object. */
  tableStyleHandle: z.string().optional(),
  /** 343 — handle of the owning BLOCK_RECORD (the `*T<N>` record). */
  blockRecordHandle: z.string().optional(),
  /** 90 — flag for table value. */
  valueFlag: z.number().int().optional(),
  /** 91 — number of rows. */
  rowCount: z.number().int().optional(),
  /** 92 — number of columns. */
  columnCount: z.number().int().optional(),
  /** 93 — flag for an override. */
  overrideFlag: z.number().int().optional(),
  /** 94 — flag for an override of border colour. */
  borderColorOverrideFlag: z.number().int().optional(),
  /** 95 — flag for an override of border lineweight. */
  borderLineweightOverrideFlag: z.number().int().optional(),
  /** 96 — flag for an override of border visibility. */
  borderVisibilityOverrideFlag: z.number().int().optional(),
  /**
   * 141 — row heights. READ-ONLY projection of {@link contentGroups}, which
   * stays the single write source; see the parser's matching note.
   */
  rowHeights: z.array(z.number()).default([]),
  /** 142 — column widths. Read-only projection, as above. */
  columnWidths: z.array(z.number()).default([]),
  /** The AcDbTable body after the documented header, verbatim, in file order. */
  contentGroups: z.array(dxfGroupRecordSchema).default([]),
  /** 160 — byte count of the R2018+ cached-graphics blob. */
  cachedGraphicsByteCount: z.number().int().optional(),
  /** 310 — the cached-graphics blob, one entry per DXF chunk, hex verbatim. */
  cachedGraphicsChunks: z.array(z.string()).default([]),
});
export type TableEntity = z.infer<typeof tableEntitySchema>;

/**
 * The table's rotation in DEGREES, derived from its horizontal-direction
 * vector (DXF group 11/21/31), which is how AcDbTable stores orientation —
 * there is no rotation scalar on the entity.
 *
 * ONE truth for every consumer (bounds, the render adapter, the properties
 * projection): a duplicate `atan2` at each call site would be free to drift.
 * A missing or degenerate vector means the AutoCAD default +X direction, i.e.
 * 0°, matching the DXF reference's stated (1, 0, 0) default.
 */
export function tableRotationDegrees(entity: {
  horizontalDirection?: { x: number; y: number; z?: number };
}): number {
  const dir = entity.horizontalDirection;
  if (!dir) return 0;
  if (!Number.isFinite(dir.x) || !Number.isFinite(dir.y)) return 0;
  if (dir.x === 0 && dir.y === 0) return 0;
  return (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
}

/* ── OLE2FRAME ────────────────────────────────────────────────────────── */

/**
 * An embedded OLE object, rendered as its frame.
 *
 * ## The embedded raster is deliberately NOT drawn
 *
 * The payload is an OLE compound document (CFBF) whose stream is, for the
 * common "Picture (Enhanced Metafile)" case, an EMF. Rasterising EMF needs a
 * metafile interpreter this project does not have and which is a separate
 * project, so the picture is not drawn. What IS drawn is the frame rectangle
 * between {@link upperLeft} and {@link lowerRight} — which is exactly what
 * AutoCAD itself displays when the OLE server is unavailable, i.e. the
 * documented degraded behaviour of the real application, not a stand-in this
 * project invented.
 *
 * Nobody should read the frame as a finished OLE implementation: the picture
 * inside it is missing and stays missing until an EMF rasteriser exists.
 * Everything else is real — the entity is parsed field by field, participates
 * in bounds, the spatial index, picking, selection, layer visibility and
 * properties, and round-trips because the payload is retained verbatim and
 * never decoded.
 */
export const oleFrameEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("OLE Frame"),
  /** 10/20/30 — upper-left corner of the frame, WCS. */
  upperLeft: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }),
  /** 11/21/31 — lower-right corner of the frame, WCS. */
  lowerRight: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }),
  /** 70 — OLE version number. */
  oleVersion: z.number().int().optional(),
  /** 3 — OLE object type string, e.g. "Picture (Enhanced Metafile)". */
  objectDescription: z.string().optional(),
  /** 71 — 1 = link, 2 = embedded, 3 = static. */
  oleObjectType: z.number().int().optional(),
  /** 72 — tile-mode descriptor: 0 = model space, 1 = paper space. */
  tileModeDescriptor: z.number().int().optional(),
  /** 73 — draw aspect (content / thumbnail / icon / doc-print). */
  drawAspect: z.number().int().optional(),
  /** 90 — byte count of the binary payload. */
  binaryByteCount: z.number().int().optional(),
  /** 310 — the OLE compound document, one entry per chunk. NEVER decoded. */
  binaryChunks: z.array(z.string()).default([]),
  /** 1 — end-of-OLE-data marker; AutoCAD writes the literal "OLE". */
  terminator: z.string().optional(),
});
export type OleFrameEntity = z.infer<typeof oleFrameEntitySchema>;
